import type {
  EncryptionResult,
  SymmetricCoreCrypto,
} from 'metaserver/static/js/encrypted_folder/crypto/symmetric_core_crypto';
import {
  AlgorithmNotFoundError,
  DecryptionError,
  InvalidKeyFormatError,
  UnknownError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import type {
  Algorithm,
  SymmetricKey,
  SymmetricCryptoExtras,
} from 'metaserver/static/js/encrypted_folder/crypto/types';

export const NONCE_LENGTH = 12;
export const AUTH_TAG_LEN = 16;

export class AesGCMSymmetricCoreCrypto implements SymmetricCoreCrypto {
  static algorithm: Algorithm = 'AES256_GCM_EXTRAS';

  async generateKey(): Promise<SymmetricKey> {
    try {
      const key = await crypto.subtle.generateKey({name: 'AES-GCM', length: 256}, true, [
        'encrypt',
      ]);
      const keyData = await crypto.subtle.exportKey('raw', key);
      return {
        algorithm: AesGCMSymmetricCoreCrypto.algorithm,
        keyData: new Uint8Array(keyData),
      };
    } catch (e) {
      throw new UnknownError((e as Error).message ?? 'WebCrypto key generation failed');
    }
  }

  async encrypt(
    input: Uint8Array<ArrayBuffer>,
    key: SymmetricKey,
    nonce?: Uint8Array<ArrayBuffer>
  ): Promise<EncryptionResult> {
    if (key.algorithm !== AesGCMSymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const cryptoKey = await crypto.subtle
      .importKey('raw', key.keyData, {name: 'AES-GCM', length: 256}, false, ['encrypt'])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });
    if (!nonce) {
      nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));
    }
    const authTagAndCiphertext = await crypto.subtle
      .encrypt({name: 'AES-GCM', iv: nonce}, cryptoKey, input)
      .catch((e) => {
        // None of the errors that can be thrown here and that are dependent on the input are realistic.
        // That means if something fails, then we're in a really weird state and we have no idea what it could be.
        throw new UnknownError((e as Error).message ?? 'WebCrypto encryption failed');
      });
    const authTag = new Uint8Array(authTagAndCiphertext.slice(-AUTH_TAG_LEN));
    const ciphertext = new Uint8Array(authTagAndCiphertext.slice(0, -AUTH_TAG_LEN));

    return {
      ciphertext,
      extras: {
        nonce,
        authTag,
      },
    };
  }

  async decrypt(
    ciphertext: Uint8Array<ArrayBuffer>,
    key: SymmetricKey,
    extras: SymmetricCryptoExtras
  ): Promise<Uint8Array<ArrayBuffer>> {
    if (key.algorithm !== AesGCMSymmetricCoreCrypto.algorithm) {
      throw new AlgorithmNotFoundError();
    }

    const cryptoKey = await crypto.subtle
      .importKey('raw', key.keyData, {name: 'AES-GCM', length: 256}, false, ['decrypt'])
      .catch(() => {
        throw new InvalidKeyFormatError();
      });

    const authTagAndCiphertext = new Uint8Array(ciphertext.length + extras.authTag.length);
    authTagAndCiphertext.set(ciphertext);
    authTagAndCiphertext.set(extras.authTag, ciphertext.length);
    const plaintext = await crypto.subtle
      .decrypt({name: 'AES-GCM', iv: extras.nonce}, cryptoKey, authTagAndCiphertext)
      .catch(() => {
        // The authTag verification failed. The reason could be a tempered ciphertext, a wrong key or a wrong nonce.
        throw new DecryptionError();
      });
    return new Uint8Array(plaintext);
  }

  serializeCryptoExtras(extras: SymmetricCryptoExtras): Uint8Array<ArrayBuffer> {
    const data = new Uint8Array(extras.authTag.length + extras.nonce.length);
    data.set(extras.authTag);
    data.set(extras.nonce, extras.authTag.length);
    return data;
  }

  deserializeCryptoExtras(serialized: Uint8Array): SymmetricCryptoExtras {
    const authTag = serialized.slice(0, AUTH_TAG_LEN);
    const nonce = serialized.slice(AUTH_TAG_LEN);
    return {authTag, nonce};
  }
}
